import React, { useState } from 'react';
import { useNavigate } from 'react-router';

import TextInput from '../Form/TextInput/TextInput';
import withFeatureFlags from '../FeatureFlags/withFeatureFlags';
import { IdsButton, IdsCard, IdsCardContent, IdsLink, IdsSectionNotice } from '@investec/ids-web-react';
import { fisaMaturityLearnMoreUrl, fisaMaturityPageSources } from '../MaturityNotice/MaturityNoticeConfig';
import TransferOutCancellationModal from './TransferOutCancellationModal';

/** @import {FisaProductData} from './MyAccountTypes' */

function InstantAccessCashIsaDetails(/** @type {{profile: FisaProductData}} */ { profile }) {
  const navigate = useNavigate();
  const [showTransferOutCancellationModal, setShowTransferOutCancellationModal] = useState(false);
  const transferInStatus = profile.transferIn?.status?.trim().replace(/[\s_-]+/g, '').toLowerCase();
  const hasTransferInProgress = transferInStatus === 'requested' || transferInStatus === 'inprogress';
  const transferStatusLabel = transferInStatus === 'inprogress' ? 'In progress' : 'Requested';
  const transferLastUpdated = profile.transferIn?.lastUpdatedAt
    ? new Date(profile.transferIn.lastUpdatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;
  const transferOutStatus = profile.transferOut?.status?.trim().replace(/[\s_-]+/g, '').toLowerCase();
  const hasTransferOutInProgress = transferOutStatus === 'requested' || transferOutStatus === 'inprogress';
  const hasTransferOutCancellationRequested = transferOutStatus === 'cancellationrequested';
  const hasActiveTransferOut = hasTransferOutInProgress || hasTransferOutCancellationRequested;
  const transferOutStatusLabel = hasTransferOutCancellationRequested
    ? 'Cancellation requested'
    : transferOutStatus === 'inprogress' ? 'In progress' : 'Requested';
  const transferOutLastUpdated = profile.transferOut?.lastUpdatedAt
    ? new Date(profile.transferOut.lastUpdatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  const handleTransferOutCancellationRequested = (updatedAt) => {
    if (profile.transferOut) {
      profile.transferOut.status = 'CancellationRequested';
      profile.transferOut.lastUpdatedAt = updatedAt;
    }
  };

  const viewProductDetails = (/** @type {React.MouseEvent} */ event) => {
    event.preventDefault();
    navigate(fisaMaturityLearnMoreUrl, { state: { source: fisaMaturityPageSources.DisaProductDetails } });
  };

  return (
    <div className="my-account--account-detail-box my-account__content--small-margin-bottom">
      <div className="my-account__row">
        <div className="my-account__col my-account__col--instant-cash-isa-details">
          <IdsCard>
            <IdsCardContent
              className="my-account__instant-access-cash-isa-card-content"
              heading="Account details"
              headingLevel="2"
            >
          <p className="my-account__text">
            Find out more about your {profile.productName}, including how it works, rates, key features and product terms.{' '}
            <IdsLink href={fisaMaturityLearnMoreUrl} onClick={viewProductDetails}>View product details.</IdsLink>
          </p>
          <div className="my-account__instant-access-cash-isa-textbox">
            <TextInput
              className="my-account__input"
              value="Instant Access Cash ISA"
              label="Interest paid to"
              disabled
            />
          </div>
          <IdsButton
            appearance="primary"
            size="md"
            onClick={() => { window.location.pathname = `/close-account/${profile.accountId}`; }}
            disabled={hasTransferInProgress || hasActiveTransferOut}
          >
            Close your {profile.productName}
          </IdsButton>
          {hasTransferInProgress && (
            <div className="my-account__row my-account__row--transfer-disabled-message">
              <div className="my-account__col my-account__col--full-width my-account__transfer-col">
                <IdsSectionNotice appearance="information" className="my-account__fisa-transfer-notice">
                  You&apos;ll need to cancel your ISA transfer before you can close this account
                </IdsSectionNotice>
              </div>
            </div>
          )}
          {hasTransferOutInProgress && (
            <div className="my-account__row my-account__row--transfer-disabled-message">
              <div className="my-account__col my-account__col--full-width my-account__transfer-col">
                <IdsSectionNotice appearance="information" className="my-account__fisa-transfer-notice">
                  You&apos;ll need to cancel your ISA transfer before you can close this account
                </IdsSectionNotice>
              </div>
            </div>
          )}
          {hasTransferInProgress && profile.transferIn && (
            <div className="my-account__transfer-section">
              <div className="my-account__row my-account__row--transfer-request-header">
                <div className="my-account__col my-account__col--full-width my-account__transfer-col">
                  <hr className="my-account__transfer-divider" />
                  <h2 className="my-account__transfer-section-title">Your ISA transfer request</h2>
                  <div className="my-account__transfer-request-row">
                    <span className="my-account__transfer-request-label">Transfer in</span>
                    <span className={`my-account__transfer-request-tag my-account__transfer-request-tag--${transferInStatus === 'inprogress' ? 'in-progress' : 'requested'}`}>
                      {transferStatusLabel}
                    </span>
                    {transferLastUpdated && (
                      <span className="my-account__transfer-request-date">last updated {transferLastUpdated}</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="my-account__row my-account__row--transfer-request-message">
                <div className="my-account__col my-account__col--full-width my-account__transfer-col">
                  <IdsSectionNotice
                    appearance="information"
                    className="my-account__fisa-transfer-notice"
                    title="Cancelling your transfer"
                  >
                    To cancel this transfer, please contact your existing ISA provider. If they&apos;re able to do this, they will let us know and we will update your account.
                  </IdsSectionNotice>
                </div>
              </div>
            </div>
          )}
          {hasActiveTransferOut && profile.transferOut && (
            <div className="my-account__transfer-section">
              <div className="my-account__row my-account__row--transfer-request-header">
                <div className="my-account__col my-account__col--full-width my-account__transfer-col">
                  <hr className="my-account__transfer-divider" />
                  <h2 className="my-account__transfer-section-title">Your ISA transfer request</h2>
                  <div className="my-account__transfer-request-row my-account__transfer-request-row--disa-single-line">
                    <span className="my-account__transfer-request-label">Transfer out</span>
                    <span className={`my-account__transfer-request-tag my-account__transfer-request-tag--${hasTransferOutCancellationRequested ? 'cancellation-requested' : transferOutStatus === 'inprogress' ? 'in-progress' : 'requested'}`}>
                      {transferOutStatusLabel}
                    </span>
                    {transferOutLastUpdated && <span className="my-account__transfer-request-date">last updated {transferOutLastUpdated}</span>}
                  </div>
                </div>
              </div>
              <div className="my-account__row my-account__row--transfer-request-message">
                <div className="my-account__col my-account__col--full-width my-account__transfer-col">
                  <IdsSectionNotice appearance={hasTransferOutCancellationRequested ? 'warning' : 'information'} className="my-account__fisa-transfer-notice" title={hasTransferOutCancellationRequested ? 'Cancellation requested' : 'Transfer out in progress'}>
                    {hasTransferOutCancellationRequested
                      ? <>
                        <p>You&apos;ve instructed us to cancel this transfer. We&apos;ve contacted your other ISA provider and we&apos;ll let you know when the cancellation is confirmed. This transfer will show as &apos;in progress&apos; until confirmed.</p>
                        <p>We&apos;ll also let you know if the cancellation has been unsuccessful and what to do next.</p>
                      </>
                      : <>
                        <p>You can cancel this transfer at any time. If you do, we&apos;ll contact your other ISA provider and update you once the cancellation is confirmed.</p>
                        <p>This transfer will show as &apos;in progress&apos; until confirmed. You won&apos;t be able to close this account while a transfer is in progress.</p>
                      </>}
                  </IdsSectionNotice>
                </div>
              </div>
              <div className="my-account__row">
                <div className="my-account__col my-account__col--full-width my-account__transfer-col">
                  <IdsButton
                    appearance="primary"
                    size="md"
                    onClick={() => setShowTransferOutCancellationModal(true)}
                    disabled={hasTransferOutCancellationRequested || showTransferOutCancellationModal}
                  >
                    {hasTransferOutCancellationRequested ? 'Cancellation requested' : 'Cancel my transfer request'}
                  </IdsButton>
                </div>
              </div>
            </div>
          )}
          {showTransferOutCancellationModal && (
            <TransferOutCancellationModal
              accountId={profile.accountId}
              mobileNumber={profile.mobileNumber}
              productKey="disa"
              onCancel={() => setShowTransferOutCancellationModal(false)}
              onComplete={() => setShowTransferOutCancellationModal(false)}
              onCancellationRequested={handleTransferOutCancellationRequested}
            />
          )}
            </IdsCardContent>
          </IdsCard>
        </div>
      </div>
    </div>
  );
}

export default withFeatureFlags(InstantAccessCashIsaDetails);
